import type { AppSettings } from '../models/appSettings';
import { defaultAppSettings } from '../models/appSettings';

/**
 * Service for loading and managing application settings from appsettings files
 */
export class AppSettingsService {
  private static instance: AppSettingsService;
  private settings: AppSettings | null = null;
  private isLoaded: boolean = false;

  private constructor() {}

  public static getInstance(): AppSettingsService {
    if (!AppSettingsService.instance) {
      AppSettingsService.instance = new AppSettingsService();
    }
    return AppSettingsService.instance;
  }

  /**
   * Loads application settings from the appropriate appsettings file
   * @returns Promise<AppSettings> - The loaded settings
   */
  public async loadSettings(): Promise<AppSettings> {
    if (this.isLoaded && this.settings) {
      return this.settings;
    }

    try {
      const environment = this.getEnvironment();
      const settingsFileName = environment === 'development'
        ? 'appsettings.development.json'
        : 'appSettings.json'; // The build pipeline should rename appSettings.Release.json to appSettings.json

      const response = await fetch(`/assets/${settingsFileName}`);

      if (!response.ok) {
        // If settings file not found, check if we can load from root (fallback for deployed environments)
        const fallbackResponse = await fetch(`/${settingsFileName}`);
        if (!fallbackResponse.ok) {
          this.settings = { ...defaultAppSettings };
        } else {
          const loadedSettings = await fallbackResponse.json();
          this.settings = this.mergeWithDefaults(loadedSettings);
        }
      } else {
        const loadedSettings = await response.json();
        this.settings = this.mergeWithDefaults(loadedSettings);
      }

      this.isLoaded = true;
      this.updateWindowGlobals();

      return this.settings!;
    } catch (error) {
      this.settings = { ...defaultAppSettings };
      this.isLoaded = true;
      this.updateWindowGlobals();
      return this.settings;
    }
  }

  /**
   * Gets the current settings (loads them if not already loaded)
   * @returns Promise<AppSettings> - The current settings
   */
  public async getSettings(): Promise<AppSettings> {
    if (!this.isLoaded) {
      return await this.loadSettings();
    }
    return this.settings || defaultAppSettings;
  }

  /**
   * Gets a specific setting value by path
   * @param path - Dot notation path to the setting (e.g., 'Api.BaseUrl')
   * @returns Promise<any> - The setting value
   */
  public async getSetting<T = any>(path: string): Promise<T> {
    const settings = await this.getSettings();
    return this.getNestedValue(settings, path);
  }

  /**
   * Gets the current environment name from loaded settings
   * @returns string - The environment name
   */
  public async getEnvironmentName(): Promise<string> {
    const settings = await this.getSettings();
    return settings.App.Environment;
  }

  /**
   * Determines the current environment for file loading
   * Only use development.json for local testing (localhost/127.0.0.1)
   * All other environments use appSettings.json (after token replacement)
   * @returns string - The environment name for file selection
   */
  private getEnvironment(): string {
    // Only use development settings for true local development
    if (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1') {
      return 'development';
    }
    // All other environments (Firebase, staging, production, dev domains, etc.) use main appSettings.json
    return 'production';
  }

  /**
   * Checks if the loaded settings contain unreplaced pipeline tokens
   * @param settings - Settings to check
   * @returns boolean - True if tokens are detected
   */
  private hasUnreplacedTokens(settings: any): boolean {
    const settingsString = JSON.stringify(settings);
    return settingsString.includes('#{') && settingsString.includes('}');
  }

  /**
   * Merges loaded settings with defaults to ensure all properties exist
   * @param loadedSettings - Settings loaded from file
   * @returns AppSettings - Merged settings
   */
  private mergeWithDefaults(loadedSettings: Partial<AppSettings>): AppSettings {
    // If loaded settings have unreplaced tokens, use defaults instead
    if (this.hasUnreplacedTokens(loadedSettings)) {
      return { ...defaultAppSettings };
    }

    return {
      Api: { ...defaultAppSettings.Api, ...loadedSettings.Api },
      Auth: { ...defaultAppSettings.Auth, ...loadedSettings.Auth },
      App: { ...defaultAppSettings.App, ...loadedSettings.App },
      Services: {
        ...defaultAppSettings.Services,
        ...loadedSettings.Services,
        MockSettings: {
          ...defaultAppSettings.Services.MockSettings,
          ...loadedSettings.Services?.MockSettings
        },
        RealTimeSettings: {
          ...defaultAppSettings.Services.RealTimeSettings,
          ...loadedSettings.Services?.RealTimeSettings
        }
      },
      Features: { ...defaultAppSettings.Features, ...loadedSettings.Features }
    };
  }

  /**
   * Updates window global variables for backward compatibility
   */
  private updateWindowGlobals(): void {
    if (!this.settings) return;

    // Update existing window globals
    (window as any).WEB_API_URL = this.settings.Api.BaseUrl;
    (window as any).APIGEE_BASE_URL = this.settings.Api.ApigeeBaseUrl;
    (window as any).WEB_CLIENT_URL = this.settings.App.ClientUrl;
    (window as any).IDENTITY_SERVER_URL = this.settings.Auth.IdentityServerUrl;
    (window as any).AUTH_SERVER_URL = this.settings.Auth.AuthServerUrl;
    (window as any).WEB_APP_BASE_NAME = this.settings.App.BaseName;
  }

  /**
   * Gets a nested value from an object using dot notation
   * @param obj - Object to search
   * @param path - Dot notation path
   * @returns any - The value at the path
   */
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current && current[key], obj);
  }

  /**
   * Reloads settings (useful for configuration updates)
   * @returns Promise<AppSettings> - The reloaded settings
   */
  public async reloadSettings(): Promise<AppSettings> {
    this.isLoaded = false;
    this.settings = null;
    return await this.loadSettings();
  }
}

// Export singleton instance
export const appSettingsService = AppSettingsService.getInstance();